import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Report } from 'notiflix/build/notiflix-report-aio'
import {
    FaArrowLeft,
    FaFilePdf,
    FaFileAlt,
    FaSignature,
    FaCode,
    FaCheckCircle,
    FaTimesCircle,
    FaExternalLinkAlt,
    FaCopy,
    FaCheck
} from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { JsonTree } from './JsonTree'
import styles from './styles.module.scss'

interface ClaimEntry {
    caseRef: string
    policyNumber: string
    lender: string
    lenderResolved: string | null
    loanValue: number | null
    monthlyRepayment: number | null
    lengthOfContract: string | number | null
    startDate: string | null
    quantum: number | null
    claimRejectionReason: string | null
    claimRejectionDetails: Record<string, unknown> | null
    raw?: Record<string, unknown>
}

interface ClientBreakdownEntry {
    clientKey: string
    introducer: string
    introducerClientId: string | number | null
    name: string
    email: string
    ccjs: number
    voluntaryArrangements: number
    cfaPresent: boolean
    clientTotalValue: number
    claimCount: number
    eligibleClaimCount: number
    clientRejectionReason: string | null
    clientRejectionDetails: Record<string, unknown> | null
    claims: ClaimEntry[]
    rawClient?: Record<string, unknown>
    rawSource?: Record<string, unknown>
}

interface DetailResponse {
    success: boolean
    introducer: string
    introducerClientId: string
    status: 'eligible' | 'ineligible'
    params: {
        commissionPercentage: number
        statutoryInterestPercentage: number
        minimumClientValue: number
        calculationDate: string
    }
    client: ClientBreakdownEntry
    message?: string
}

interface NavState {
    introducer?: string
    introducerClientId?: string | number
}

const CLIENT_REASON_LABELS: Record<string, string> = {
    clientsWithCcjOrVA: 'CCJ or Voluntary Arrangement',
    clientsExcludedMissingCfa: 'Missing CFA / signature date',
    clientsBelowMinimum: 'Below £1,700 minimum client value',
    clientsWithDuplicateEmail: 'Duplicate email in funder DB',
    clientsAllClaimsRejected: 'All claims rejected (no eligible value)'
}

const CLAIM_REASON_LABELS: Record<string, string> = {
    claimsExcludedDuplicatePolicyNumber: 'Duplicate policy number',
    claimsExcludedPre2007: 'Pre-2007 start date',
    claimsExcludedBelowMinLoan: 'Below £5,000 minimum loan',
    claimsExcludedUnapprovedLender: 'Unapproved lender',
    claimsExcludedNoLengthOfContract: 'Missing length of contract',
    claimsExcludedBelowMinContractLength: 'Contract length under 5 months',
    claimsExcludedBelowMinMonthlyRepayment: 'Monthly repayment < £50',
    claimsExcludedExcessiveMonthlyRepayment: 'Monthly repayment > £1,500'
}

const formatMoney = (v: number | null | undefined) => {
    if (typeof v !== 'number' || !isFinite(v)) return '—'
    return `£${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatNumber = (v: number | null | undefined) => {
    if (typeof v !== 'number' || !isFinite(v)) return '—'
    return v.toLocaleString('en-GB')
}

const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—'
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const labelClientReason = (reason: string | null) =>
    reason ? CLIENT_REASON_LABELS[reason] || reason : null

const labelClaimReason = (reason: string | null) => {
    if (!reason) return null
    if (CLAIM_REASON_LABELS[reason]) return CLAIM_REASON_LABELS[reason]
    if (reason.startsWith('claimsExcludedQuantum:')) return `Quantum exclusion: ${reason.split(':')[1]}`
    return reason
}

// clientKey is `${introducer}_${client_id}`. Introducer names don't currently
// contain underscores, but they can contain spaces — split on the LAST `_` to
// be safe.
const parseClientKey = (clientKey: string) => {
    const idx = clientKey.lastIndexOf('_')
    if (idx === -1) return { introducer: '', introducerClientId: clientKey }
    return {
        introducer: clientKey.slice(0, idx),
        introducerClientId: clientKey.slice(idx + 1)
    }
}

const clientAddressFields = [
    'address_line_one',
    'address_line_two',
    'address_line_three',
    'town',
    'county',
    'postcode'
]

const buildClientAddress = (client: Record<string, unknown> | undefined) => {
    if (!client) return ''
    return clientAddressFields
        .map((k) => (client as Record<string, unknown>)[k])
        .filter((v) => v != null && String(v).trim() !== '')
        .join(', ')
}

export const ClientSchemaDetailsView = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams<{ clientKey: string }>()

    const navState = (location.state || {}) as NavState
    const fromKey = parseClientKey(decodeURIComponent(params.clientKey || ''))
    const introducer =
        (typeof navState.introducer === 'string' && navState.introducer) || fromKey.introducer
    const introducerClientId =
        (navState.introducerClientId != null && String(navState.introducerClientId)) ||
        fromKey.introducerClientId

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [detail, setDetail] = useState<DetailResponse | null>(null)
    const [showJson, setShowJson] = useState(false)
    const [jsonCopied, setJsonCopied] = useState(false)
    const [actionLoading, setActionLoading] = useState<
        null | 'creditReport' | 'signature' | 'creditJson'
    >(null)
    const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
    const [signatureSigned, setSignatureSigned] = useState<number | null>(null)
    const [signatureDate, setSignatureDate] = useState<string | null>(null)
    const [signatureError, setSignatureError] = useState<string | null>(null)
    const [showSignature, setShowSignature] = useState(false)

    const [creditJsonData, setCreditJsonData] = useState<unknown>(null)
    const [creditJsonKey, setCreditJsonKey] = useState<string | null>(null)
    const [creditJsonError, setCreditJsonError] = useState<string | null>(null)
    const [showCreditJson, setShowCreditJson] = useState(false)
    const [creditJsonCopied, setCreditJsonCopied] = useState(false)

    const jsonSectionRef = useRef<HTMLDivElement | null>(null)
    const signatureSectionRef = useRef<HTMLDivElement | null>(null)
    const creditJsonSectionRef = useRef<HTMLDivElement | null>(null)

    const fetchDetail = useCallback(async () => {
        if (!introducer || !introducerClientId) {
            setError('Missing introducer or client id in URL.')
            setLoading(false)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const response = await api({
                url: '/funding-schema-client-details',
                data: { introducer, introducerClientId }
            })
            const payload = response.data as DetailResponse
            if (!payload?.success) {
                throw new Error(payload?.message || 'Failed to load client')
            }
            setDetail(payload)
        } catch (err: unknown) {
            console.error('Schema client details error:', err)
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                (err as Error)?.message ||
                'Failed to load client'
            setError(message)
        } finally {
            setLoading(false)
        }
    }, [introducer, introducerClientId])

    useEffect(() => {
        fetchDetail()
    }, [fetchDetail])

    // Open a placeholder window synchronously on click. Browsers only allow
    // window.open() during a direct user gesture, so we can't wait for the
    // async API call before opening — we'd lose the user-gesture context and
    // the popup would be blocked. Instead we open about:blank now and rewrite
    // its location once the URL arrives.
    // Goes straight to S3 via /funding-schema-client-credit-report which signs
    // the URL based on the introducer's documentPaths.creditReport template.
    // The placeholder window is opened synchronously to keep the user gesture
    // and avoid popup-blocker issues; we then redirect or close it once the
    // signed URL arrives.
    const openCreditReport = useCallback(async () => {
        if (!introducer || !introducerClientId) return
        setActionLoading('creditReport')
        const placeholder = window.open('about:blank', '_blank', 'noopener,noreferrer')
        try {
            const response = await api({
                url: '/funding-schema-client-credit-report',
                data: { introducer, introducerClientId }
            })
            const url = response?.data?.url
            if (response?.data?.success && url) {
                if (placeholder) {
                    placeholder.location.href = url
                } else {
                    window.open(url, '_blank', 'noopener,noreferrer')
                }
            } else {
                if (placeholder) placeholder.close()
                Report.warning(
                    'Credit report unavailable',
                    response?.data?.message || 'No credit report on file for this client.',
                    'OKAY'
                )
            }
        } catch (err: unknown) {
            if (placeholder) placeholder.close()
            console.error('Credit report fetch failed:', err)
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                (err as Error)?.message ||
                'Failed to load credit report'
            Report.failure('Credit report failed', message, 'OKAY')
        } finally {
            setActionLoading(null)
        }
    }, [introducer, introducerClientId])

    // Fetch the raw signature PNG as a data URL and render it inline on the
    // page — no popup needed, so no popup-blocker issues. First click loads,
    // subsequent clicks just toggle the section's visibility.
    const handleSignatureToggle = useCallback(async () => {
        if (!introducer || !introducerClientId) return
        if (signatureDataUrl || signatureError) {
            setShowSignature((s) => !s)
            requestAnimationFrame(() => {
                signatureSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            })
            return
        }
        setActionLoading('signature')
        setShowSignature(true)
        try {
            const response = await api({
                url: '/funding-schema-client-signature',
                data: { introducer, introducerClientId }
            })
            if (response?.data?.success && response.data.dataUrl) {
                setSignatureDataUrl(String(response.data.dataUrl))
                setSignatureSigned(response.data.signed ?? null)
                setSignatureDate(response.data.signatureDate || null)
                setSignatureError(null)
            } else {
                setSignatureDataUrl(null)
                setSignatureError(
                    response?.data?.message || 'No signature on file for this client.'
                )
            }
        } catch (err: unknown) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                (err as Error)?.message ||
                'Failed to fetch signature'
            console.error('Signature fetch failed:', err)
            setSignatureDataUrl(null)
            setSignatureError(message)
        } finally {
            setActionLoading(null)
            requestAnimationFrame(() => {
                signatureSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            })
        }
    }, [introducer, introducerClientId, signatureDataUrl, signatureError])

    const handleJsonToggle = useCallback(() => {
        setShowJson((prev) => {
            const next = !prev
            if (next) {
                requestAnimationFrame(() => {
                    jsonSectionRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    })
                })
            }
            return next
        })
    }, [])

    const handleJsonCopy = useCallback(async () => {
        if (!detail) return
        try {
            await navigator.clipboard.writeText(JSON.stringify(detail, null, 2))
            setJsonCopied(true)
            setTimeout(() => setJsonCopied(false), 1500)
        } catch (err) {
            console.error('Failed to copy JSON', err)
        }
    }, [detail])

    // Lazily fetches the credit-report JSON sibling from S3. First click
    // loads, then renders inline; subsequent clicks just toggle visibility.
    const handleCreditJsonToggle = useCallback(async () => {
        if (!introducer || !introducerClientId) return

        if (creditJsonData !== null || creditJsonError) {
            setShowCreditJson((s) => !s)
            requestAnimationFrame(() => {
                creditJsonSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                })
            })
            return
        }

        setActionLoading('creditJson')
        setShowCreditJson(true)
        try {
            const response = await api({
                url: '/funding-schema-client-credit-report-json',
                data: { introducer, introducerClientId }
            })
            if (response?.data?.success) {
                if (response.data.parsed === false && typeof response.data.raw === 'string') {
                    setCreditJsonData({ __rawText: response.data.raw })
                } else {
                    setCreditJsonData(response.data.data ?? null)
                }
                setCreditJsonKey(response.data.key || null)
                setCreditJsonError(null)
            } else {
                setCreditJsonData(null)
                setCreditJsonKey(null)
                setCreditJsonError(
                    response?.data?.message || 'No credit report JSON on file for this client.'
                )
            }
        } catch (err: unknown) {
            const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                (err as Error)?.message ||
                'Failed to fetch credit report JSON'
            console.error('Credit report JSON fetch failed:', err)
            setCreditJsonData(null)
            setCreditJsonKey(null)
            setCreditJsonError(message)
        } finally {
            setActionLoading(null)
            requestAnimationFrame(() => {
                creditJsonSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                })
            })
        }
    }, [introducer, introducerClientId, creditJsonData, creditJsonError])

    const handleCreditJsonCopy = useCallback(async () => {
        if (creditJsonData == null) return
        try {
            const text =
                typeof (creditJsonData as { __rawText?: string }).__rawText === 'string'
                    ? (creditJsonData as { __rawText: string }).__rawText
                    : JSON.stringify(creditJsonData, null, 2)
            await navigator.clipboard.writeText(text)
            setCreditJsonCopied(true)
            setTimeout(() => setCreditJsonCopied(false), 1500)
        } catch (err) {
            console.error('Failed to copy credit JSON', err)
        }
    }, [creditJsonData])

    const client = detail?.client
    const isEligible = detail?.status === 'eligible'

    const fullAddress = useMemo(() => buildClientAddress(client?.rawClient), [client?.rawClient])

    const personalInfo = useMemo(() => {
        if (!client) return [] as Array<{ label: string; value: string }>
        const rc = (client.rawClient || {}) as Record<string, unknown>
        const rs = (client.rawSource || {}) as Record<string, unknown>
        const phone = (rc.phone || rs.phone || rs.mobile_number) as string | undefined
        const dob = (rc.dob || rs.dob || rs.date_of_birth) as string | undefined
        return [
            { label: 'Introducer', value: client.introducer || '—' },
            { label: 'Introducer client id', value: String(client.introducerClientId || '—') },
            { label: 'Email', value: client.email || '—' },
            { label: 'Phone', value: phone ? String(phone) : '—' },
            { label: 'Date of birth', value: dob ? formatDate(String(dob)) : '—' },
            { label: 'Address', value: fullAddress || '—' }
        ]
    }, [client, fullAddress])

    const summaryStats = useMemo(() => {
        if (!client) return [] as Array<{ label: string; value: string; tone?: 'ok' | 'warn' }>
        return [
            { label: 'Total client value', value: formatMoney(client.clientTotalValue) },
            { label: 'Total claims', value: formatNumber(client.claimCount) },
            {
                label: 'Eligible claims',
                value: formatNumber(client.eligibleClaimCount),
                tone:
                    client.eligibleClaimCount === client.claimCount
                        ? ('ok' as const)
                        : ('warn' as const)
            },
            {
                label: 'CFA',
                value: client.cfaPresent ? 'On file' : 'Missing',
                tone: client.cfaPresent ? ('ok' as const) : ('warn' as const)
            },
            {
                label: 'CCJs',
                value: formatNumber(client.ccjs),
                tone: client.ccjs > 0 ? ('warn' as const) : undefined
            },
            {
                label: 'Voluntary arrangements',
                value: formatNumber(client.voluntaryArrangements),
                tone: client.voluntaryArrangements > 0 ? ('warn' as const) : undefined
            }
        ]
    }, [client])

    const handleBack = useCallback(() => {
        navigate('/analyse-schema')
    }, [navigate])

    if (loading) {
        return (
            <div className={styles.detailsContainer}>
                <div className={styles.loadingState}>Loading client…</div>
            </div>
        )
    }

    if (error || !detail || !client) {
        return (
            <div className={styles.detailsContainer}>
                <button type="button" className={styles.backLink} onClick={handleBack}>
                    <FaArrowLeft /> Back to analysis
                </button>
                <div className={styles.errorBanner}>{error || 'Client not found.'}</div>
            </div>
        )
    }

    const clientReasonLabel = labelClientReason(client.clientRejectionReason)

    return (
        <div className={styles.detailsContainer}>
            <button type="button" className={styles.backLink} onClick={handleBack}>
                <FaArrowLeft /> Back to analysis
            </button>

            <div className={styles.headerCard}>
                <div className={styles.headerMain}>
                    <h2 className={styles.clientName}>{client.name || '—'}</h2>
                    <div className={styles.headerSub}>
                        {client.email || '—'} · {client.introducer} #{client.introducerClientId}
                    </div>
                </div>
                <div
                    className={`${styles.statusChip} ${
                        isEligible ? styles.statusChipOk : styles.statusChipWarn
                    }`}
                >
                    {isEligible ? <FaCheckCircle /> : <FaTimesCircle />}
                    {isEligible ? 'Eligible' : 'Ineligible for funding'}
                </div>
            </div>

            <div className={styles.actionsRow}>
                <button
                    type="button"
                    className={styles.actionButton}
                    onClick={openCreditReport}
                    disabled={actionLoading !== null}
                >
                    <FaFilePdf />
                    {actionLoading === 'creditReport' ? 'Loading…' : 'View credit report'}
                    {actionLoading !== 'creditReport' && (
                        <FaExternalLinkAlt className={styles.actionIconSm} />
                    )}
                </button>
                <button
                    type="button"
                    className={styles.actionButton}
                    onClick={handleSignatureToggle}
                    disabled={actionLoading !== null}
                >
                    <FaSignature />
                    {actionLoading === 'signature'
                        ? 'Loading…'
                        : showSignature && (signatureDataUrl || signatureError)
                          ? 'Hide signature'
                          : 'View signature'}
                </button>
                <button
                    type="button"
                    className={styles.actionButton}
                    onClick={handleCreditJsonToggle}
                    disabled={actionLoading !== null}
                >
                    <FaFileAlt />
                    {actionLoading === 'creditJson'
                        ? 'Loading…'
                        : showCreditJson && (creditJsonData !== null || creditJsonError)
                          ? 'Hide credit report data'
                          : 'View credit report data'}
                </button>
                <button
                    type="button"
                    className={`${styles.actionButton} ${styles.actionButtonGhost}`}
                    onClick={handleJsonToggle}
                >
                    <FaCode />
                    {showJson ? 'Hide raw JSON' : 'Show raw JSON'}
                </button>
            </div>

            {showSignature && (
                <section className={styles.section} ref={signatureSectionRef}>
                    <h3 className={styles.sectionTitle}>Signature</h3>
                    {actionLoading === 'signature' && !signatureDataUrl && !signatureError && (
                        <div className={styles.signatureLoading}>Loading signature…</div>
                    )}
                    {signatureError && (
                        <div className={styles.signatureError}>{signatureError}</div>
                    )}
                    {signatureDataUrl && (
                        <div className={styles.signatureBox}>
                            <img
                                src={signatureDataUrl}
                                alt="Client signature"
                                className={styles.signatureImage}
                            />
                            <div className={styles.signatureMeta}>
                                <div>
                                    <span className={styles.signatureMetaLabel}>Signed</span>{' '}
                                    {signatureSigned === 1 ? 'Yes' : 'No'}
                                </div>
                                <div>
                                    <span className={styles.signatureMetaLabel}>Signed at</span>{' '}
                                    {signatureDate ? formatDate(signatureDate) : '—'}
                                </div>
                                <a
                                    href={signatureDataUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.signatureOpenLink}
                                >
                                    Open in new tab <FaExternalLinkAlt />
                                </a>
                            </div>
                        </div>
                    )}
                </section>
            )}

            {showCreditJson && (
                <section className={styles.section} ref={creditJsonSectionRef}>
                    <div className={styles.jsonHeader}>
                        <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
                            Credit report data
                        </h3>
                        {creditJsonData !== null && (
                            <button
                                type="button"
                                className={styles.jsonCopyButton}
                                onClick={handleCreditJsonCopy}
                                aria-label="Copy credit report JSON to clipboard"
                            >
                                {creditJsonCopied ? <FaCheck /> : <FaCopy />}
                                {creditJsonCopied ? ' Copied' : ' Copy JSON'}
                            </button>
                        )}
                    </div>
                    {creditJsonKey && (
                        <p className={styles.sectionHint}>
                            Loaded from <code>{creditJsonKey}</code>
                        </p>
                    )}
                    {actionLoading === 'creditJson' &&
                        creditJsonData === null &&
                        !creditJsonError && (
                            <div className={styles.signatureLoading}>
                                Loading credit report JSON…
                            </div>
                        )}
                    {creditJsonError && (
                        <div className={styles.signatureError}>{creditJsonError}</div>
                    )}
                    {creditJsonData !== null &&
                        (typeof (creditJsonData as { __rawText?: string }).__rawText ===
                        'string' ? (
                            <pre className={styles.rawJson}>
                                {(creditJsonData as { __rawText: string }).__rawText}
                            </pre>
                        ) : (
                            <JsonTree data={creditJsonData} initialOpenDepth={2} />
                        ))}
                </section>
            )}

            <div className={styles.summaryGrid}>
                {summaryStats.map((s) => (
                    <div key={s.label} className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>{s.label}</span>
                        <span
                            className={`${styles.summaryValue} ${
                                s.tone === 'ok'
                                    ? styles.summaryValueOk
                                    : s.tone === 'warn'
                                      ? styles.summaryValueWarn
                                      : ''
                            }`}
                        >
                            {s.value}
                        </span>
                    </div>
                ))}
            </div>

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Client information</h3>
                <div className={styles.personalGrid}>
                    {personalInfo.map((p) => (
                        <div key={p.label} className={styles.personalItem}>
                            <span className={styles.personalLabel}>{p.label}</span>
                            <span className={styles.personalValue}>{p.value}</span>
                        </div>
                    ))}
                </div>
            </section>

            {!isEligible && (clientReasonLabel || client.clientRejectionDetails) && (
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Why this client failed</h3>
                    {clientReasonLabel && (
                        <div className={styles.reasonRow}>
                            <span className={`${styles.chip} ${styles.chipClient}`}>
                                {clientReasonLabel}
                            </span>
                        </div>
                    )}
                    {client.clientRejectionDetails && (
                        <div className={styles.detailGrid}>
                            {Object.entries(client.clientRejectionDetails).map(([k, v]) => (
                                <div key={k} className={styles.detailItem}>
                                    <span className={styles.detailLabel}>{k}</span>
                                    <span className={styles.detailValue}>
                                        {typeof v === 'number' && /value|required|monthly/i.test(k)
                                            ? formatMoney(v as number)
                                            : String(v ?? '—')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                    Claims ({client.claims.length})
                    {!isEligible && (
                        <span className={styles.sectionHint}>
                            {' '}
                            — every claim is shown so you can see which were eligible vs. rejected
                        </span>
                    )}
                </h3>
                <div className={styles.claimsList}>
                    {client.claims.map((claim, idx) => {
                        const reasonLabel = labelClaimReason(claim.claimRejectionReason)
                        const isClaimRejected = !!claim.claimRejectionReason
                        return (
                            <ClaimCard
                                key={`${claim.caseRef || idx}-${claim.policyNumber || idx}`}
                                claim={claim}
                                reasonLabel={reasonLabel}
                                isRejected={isClaimRejected}
                            />
                        )
                    })}
                </div>
            </section>

            {showJson && (
                <section className={styles.section} ref={jsonSectionRef}>
                    <div className={styles.jsonHeader}>
                        <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
                            Raw data
                        </h3>
                        <button
                            type="button"
                            className={styles.jsonCopyButton}
                            onClick={handleJsonCopy}
                            aria-label="Copy raw JSON to clipboard"
                        >
                            {jsonCopied ? <FaCheck /> : <FaCopy />}
                            {jsonCopied ? ' Copied' : ' Copy JSON'}
                        </button>
                    </div>
                    <p className={styles.sectionHint}>
                        Full eligibility breakdown plus the underlying database rows for the client
                        and each claim.
                    </p>
                    <pre className={styles.rawJson}>{JSON.stringify(detail, null, 2)}</pre>
                </section>
            )}
        </div>
    )
}

interface ClaimCardProps {
    claim: ClaimEntry
    reasonLabel: string | null
    isRejected: boolean
}

const ClaimCard = ({ claim, reasonLabel, isRejected }: ClaimCardProps) => {
    const [showRaw, setShowRaw] = useState(false)
    const stats = [
        { label: 'Loan value', value: formatMoney(claim.loanValue) },
        {
            label: 'Monthly repayment',
            value: claim.monthlyRepayment != null ? formatMoney(claim.monthlyRepayment) : '—'
        },
        {
            label: 'Term',
            value:
                claim.lengthOfContract != null && claim.lengthOfContract !== ''
                    ? `${claim.lengthOfContract} months`
                    : '—'
        },
        { label: 'Start date', value: formatDate(claim.startDate) },
        { label: 'Quantum', value: formatMoney(claim.quantum) }
    ]

    return (
        <div className={`${styles.claimCard} ${isRejected ? styles.claimCardRejected : ''}`}>
            <div className={styles.claimHeader}>
                <div className={styles.claimHeaderMain}>
                    <span className={styles.claimRef}>{claim.caseRef || '—'}</span>
                    <span className={styles.claimPolicy}>
                        Policy {claim.policyNumber || '—'}
                    </span>
                </div>
                <div className={styles.claimHeaderRight}>
                    <span className={styles.claimLender} title={claim.lender || ''}>
                        {claim.lender || '—'}
                    </span>
                    {claim.lenderResolved && claim.lenderResolved !== claim.lender && (
                        <span className={styles.claimLenderResolved}>
                            → {claim.lenderResolved}
                        </span>
                    )}
                </div>
                <div className={styles.claimStatus}>
                    {isRejected ? (
                        <span className={`${styles.chip} ${styles.chipClaim}`}>
                            {reasonLabel || 'Rejected'}
                        </span>
                    ) : (
                        <span className={`${styles.chip} ${styles.chipOk}`}>Eligible</span>
                    )}
                </div>
            </div>

            <div className={styles.claimStatsGrid}>
                {stats.map((s) => (
                    <div key={s.label} className={styles.claimStatItem}>
                        <span className={styles.claimStatLabel}>{s.label}</span>
                        <span className={styles.claimStatValue}>{s.value}</span>
                    </div>
                ))}
            </div>

            {claim.claimRejectionDetails && (
                <div className={styles.detailGrid}>
                    {Object.entries(claim.claimRejectionDetails).map(([k, v]) => (
                        <div key={k} className={styles.detailItem}>
                            <span className={styles.detailLabel}>{k}</span>
                            <span className={styles.detailValue}>
                                {typeof v === 'number' && /value|required|monthly|quantum/i.test(k)
                                    ? formatMoney(v as number)
                                    : String(v ?? '—')}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {claim.raw && (
                <div className={styles.claimRawToggle}>
                    <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => setShowRaw((s) => !s)}
                    >
                        {showRaw ? 'Hide raw row' : 'Show raw database row'}
                    </button>
                    {showRaw && (
                        <pre className={styles.rawJson}>
                            {JSON.stringify(claim.raw, null, 2)}
                        </pre>
                    )}
                </div>
            )}
        </div>
    )
}
